import _extends from "@babel/runtime/helpers/extends";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import * as React from 'react';
import styled from 'styled-components';
import { useDrop, useDrag } from 'react-dnd';
import { Popover, List, ListItem, Divider } from '@material-ui/core';
import Button from '../Button';
import Checkbox from '../Checkbox';
import Typography from '../Typography';
import { useEmphasis } from '../utils';
var StyledColumnControlContainer = styled.span.withConfig({
  displayName: "ColumnControlPopoverList__StyledColumnControlContainer",
  componentId: "sc-1klhjrw-0"
})(["margin-left:", ";border-left:", ";padding-left:", ";padding-right:8px;"], function (_ref) {
  var $hasActions = _ref.$hasActions;
  return $hasActions ? '8px' : 0;
}, function (_ref2) {
  var $hasActions = _ref2.$hasActions,
      $color = _ref2.$color;
  return $hasActions ? "1px solid ".concat($color) : 'none';
}, function (_ref3) {
  var $hasActions = _ref3.$hasActions;
  return $hasActions ? '8px' : 0;
});
var StyledHeaderTextWrapper = styled(Typography).withConfig({
  displayName: "ColumnControlPopoverList__StyledHeaderTextWrapper",
  componentId: "sc-1klhjrw-1"
})(["display:flex;justify-content:center;align-items:center;padding:8px 8px 0px;"]);
var DRAGGABLE_ITEM = 'DRAGGABLE_ITEM';
export var ColumnControlPopoverList = function ColumnControlPopoverList(_ref4) {
  var e2e = _ref4.e2e,
      inputColumns = _ref4.columns,
      hasActions = _ref4.hasActions,
      onColumnsChange = _ref4.onColumnsChange,
      onColumnControlPopoverClick = _ref4.onColumnControlPopoverClick;

  var _React$useState = React.useState(null),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      anchorEl = _React$useState2[0],
      setAnchorEl = _React$useState2[1];

  var _React$useState3 = React.useState([]),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      columns = _React$useState4[0],
      setColumns = _React$useState4[1];

  var emphasis = useEmphasis();
  React.useEffect(function () {
    setColumns(inputColumns);
  }, [inputColumns]);

  var handleClick = function handleClick(event) {
    if (onColumnControlPopoverClick) {
      onColumnControlPopoverClick();
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  var handleClose = function handleClose() {
    setAnchorEl(null);
  };

  var open = Boolean(anchorEl);
  var id = open ? 'simple-popover' : undefined;
  var handleMoveItem = React.useCallback(function (dragIndex, hoverIndex) {
    var dragCard = columns[dragIndex];

    var newColumns = _toConsumableArray(columns);

    newColumns.splice(dragIndex, 1);
    newColumns.splice(hoverIndex, 0, dragCard);
    setColumns(newColumns);
  }, [columns]);

  var _useDrop = useDrop({
    accept: DRAGGABLE_ITEM,
    drop: function drop() {
      onColumnsChange(columns);
    }
  }),
      _useDrop2 = _slicedToArray(_useDrop, 2),
      drop = _useDrop2[1];

  return /*#__PURE__*/React.createElement(StyledColumnControlContainer, {
    $color: emphasis.contextHigh,
    $hasActions: hasActions
  }, /*#__PURE__*/React.createElement(Button, _extends({}, e2e && {
    e2e: "".concat(e2e, "-table-customize-columns")
  }, {
    "aria-describedby": id,
    variant: "contained",
    icon: "table_chart",
    tooltip: "Customize Columns",
    dsOnClick: handleClick
  })), /*#__PURE__*/React.createElement(Popover, {
    id: id,
    open: open,
    anchorEl: anchorEl,
    onClose: handleClose,
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left'
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left'
    }
  }, /*#__PURE__*/React.createElement(StyledHeaderTextWrapper, null, "Customize Columns"), /*#__PURE__*/React.createElement(List // aria-label=""
  , {
    ref: drop,
    dense: true
  }, /*#__PURE__*/React.createElement(Divider, null), columns.map(function (column, index) {
    return (
      /*#__PURE__*/

      /* eslint-disable @typescript-eslint/no-use-before-define */
      React.createElement(DraggableListItem, {
        e2e: e2e,
        key: "column-control-list-item-".concat(column.field),
        index: index,
        column: column,
        onMoveItem: handleMoveItem,
        onToggleItem: function onToggleItem(index) {
          var newColumns = _toConsumableArray(columns);

          newColumns[index].hidden = !newColumns[index].hidden;
          setColumns(newColumns);
          onColumnsChange(newColumns);
        }
      })
    );
  }))));
};

var DraggableListItem = function DraggableListItem(_ref5) {
  var e2e = _ref5.e2e,
      index = _ref5.index,
      column = _ref5.column,
      onMoveItem = _ref5.onMoveItem,
      onToggleItem = _ref5.onToggleItem;
  var ref = React.useRef(null);

  var _useDrop3 = useDrop({
    accept: DRAGGABLE_ITEM,
    hover: function hover(item, monitor) {
      var _ref$current;

      if (!ref.current) {
        return;
      }

      var dragIndex = item.index;
      var hoverIndex = index; // Don't replace items with themselves

      if (dragIndex === hoverIndex) {
        return;
      } // Determine rectangle on screen


      var hoverBoundingRect = (_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.getBoundingClientRect(); // Get vertical middle

      var hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2; // Determine mouse position

      var clientOffset = monitor.getClientOffset(); // Get pixels to the top

      var hoverClientY = clientOffset.y - hoverBoundingRect.top; // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      // Dragging downwards

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      } // Dragging upwards


      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      } // Time to actually perform the action


      onMoveItem(dragIndex, hoverIndex); // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.

      item.index = hoverIndex;
    }
  }),
      _useDrop4 = _slicedToArray(_useDrop3, 2),
      drop = _useDrop4[1];

  var _useDrag = useDrag({
    item: {
      type: DRAGGABLE_ITEM,
      index: index
    },
    collect: function collect(monitor) {
      return {
        isDragging: monitor.isDragging()
      };
    }
  }),
      _useDrag2 = _slicedToArray(_useDrag, 3),
      isDragging = _useDrag2[0].isDragging,
      drag = _useDrag2[1],
      preview = _useDrag2[2];

  drag(drop(preview(ref)));
  var opacity = isDragging ? 0 : 1;
  return /*#__PURE__*/React.createElement("span", {
    ref: ref
  }, /*#__PURE__*/React.createElement(ListItem, {
    ref: function ref(node) {
      return preview(node);
    },
    dense: true,
    disableGutters: true,
    style: {
      opacity: opacity
    }
  }, /*#__PURE__*/React.createElement(Button, {
    buttonRef: function buttonRef(node) {
      drag(node);
    },
    variant: "contained",
    icon: "drag_indicator"
  }), /*#__PURE__*/React.createElement(Checkbox, _extends({}, e2e && {
    e2e: "".concat(e2e, "-table-customize-column-").concat(column.label || column.field, "-checkbox")
  }, {
    label: column.label || column.field,
    checked: !column.hidden,
    dsOnChange: function dsOnChange() {
      onToggleItem(index);
    }
  }))), /*#__PURE__*/React.createElement(Divider, null));
};

try {
  ColumnControlPopoverList.__docgenInfo = {
    description: '',
    displayName: 'ColumnControlPopoverList',
    methods: [],
    props: {
      e2e: {
        defaultValue: null,
        description: '',
        name: 'e2e',
        required: false,
        type: {
          name: 'string'
        }
      },
      columns: {
        defaultValue: null,
        description: '',
        name: 'columns',
        required: true,
        type: {
          name: 'any[]'
        }
      },
      hasActions: {
        defaultValue: null,
        description: '',
        name: 'hasActions',
        required: true,
        type: {
          name: 'boolean'
        }
      },
      onColumnsChange: {
        defaultValue: null,
        description: '',
        name: 'onColumnsChange',
        required: true,
        type: {
          name: '(columns: any[]) => void'
        }
      },
      onColumnControlPopoverClick: {
        defaultValue: null,
        description: '',
        name: 'onColumnControlPopoverClick',
        required: false,
        type: {
          name: '() => void'
        }
      }
    }
  };
} catch (e) {}
//# sourceMappingURL=ColumnControlPopoverList.js.map